// Actes du dépôt côté usager (POST /usager/dossiers, PUT, pièces, dépôt) : chaque réponse est la
// fiche complète du dossier, placée dans le cache ; les erreurs 400 sont rendues par champ.
import type { DonneesFormulaire } from '@cc/contrats';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { requete } from '../../../api/client';
import { ErreurApi, messageErreur } from '../../../api/erreurs';
import { CLES } from '../api';
import type { FicheDossierUsager } from '../types';

export interface PieceCoffreChoisie {
  piece_id: string;
  type: string;
}

export function useDepot(dossierId: string | null) {
  const client = useQueryClient();
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  const placer = useCallback(
    (fiche: FicheDossierUsager) => {
      client.setQueryData(CLES.dossier(fiche.id), fiche);
      void client.invalidateQueries({ queryKey: CLES.dossiers });
      return fiche;
    },
    [client],
  );

  const executer = useCallback(
    async <T,>(nom: string, fn: () => Promise<T>): Promise<T> => {
      setEnCours(nom);
      setErreur(null);
      try {
        return await fn();
      } catch (e) {
        setErreur(e);
        throw e;
      } finally {
        setEnCours(null);
      }
    },
    [],
  );

  const creer = useCallback(
    (collectivite: string, demarche: string, representationId: string | null, donnees: DonneesFormulaire) =>
      executer('creer', () =>
        requete<FicheDossierUsager>('/usager/dossiers', {
          methode: 'POST',
          corps: { collectivite, demarche, ...(representationId ? { representation_id: representationId } : {}), donnees_formulaire: donnees },
        }).then(placer),
      ),
    [executer, placer],
  );

  const enregistrer = useCallback(
    (donnees: DonneesFormulaire) =>
      executer('enregistrer', () => requete<FicheDossierUsager>(`/usager/dossiers/${dossierId}`, { methode: 'PUT', corps: { donnees_formulaire: donnees } }).then(placer)),
    [dossierId, executer, placer],
  );

  const attacherCoffre = useCallback(
    (pieces: PieceCoffreChoisie[]) =>
      executer('coffre', () => requete<FicheDossierUsager>(`/usager/dossiers/${dossierId}`, { methode: 'PUT', corps: { pieces_coffre: pieces } }).then(placer)),
    [dossierId, executer, placer],
  );

  const televerser = useCallback(
    (fichier: File, type: string, titre?: string) =>
      executer('fichier', () => {
        const corps = new FormData();
        corps.set('fichier', fichier);
        corps.set('type', type);
        if (titre) corps.set('titre', titre);
        return requete<FicheDossierUsager>(`/usager/dossiers/${dossierId}/pieces`, { methode: 'POST', corps }).then(placer);
      }),
    [dossierId, executer, placer],
  );

  // Le dépôt produit l'accusé de dépôt : la liste des notifications (et le point de l'en-tête) est rechargée.
  const deposer = useCallback(
    () =>
      executer('deposer', () =>
        requete<FicheDossierUsager>(`/usager/dossiers/${dossierId}/deposer`, { methode: 'POST' })
          .then(placer)
          .then((fiche) => {
            void client.invalidateQueries({ queryKey: CLES.notifications });
            return fiche;
          }),
      ),
    [client, dossierId, executer, placer],
  );

  const completer = useCallback(
    (reponses: DonneesFormulaire, pieces: PieceCoffreChoisie[], message: string) =>
      executer('completer', () =>
        requete<FicheDossierUsager>(`/usager/dossiers/${dossierId}/complement`, { methode: 'POST', corps: { reponses, pieces_coffre: pieces, ...(message.trim() ? { message: message.trim() } : {}) } }).then(placer),
      ),
    [dossierId, executer, placer],
  );

  const retirer = useCallback(
    (motif: string) => executer('retirer', () => requete<FicheDossierUsager>(`/usager/dossiers/${dossierId}/retirer`, { methode: 'POST', corps: { motif } }).then(placer)),
    [dossierId, executer, placer],
  );

  return {
    enCours,
    erreur,
    messageErreur: erreur ? messageErreur(erreur) : null,
    detailsErreur: erreur instanceof ErreurApi ? erreur.champs : [],
    codeErreur: erreur instanceof ErreurApi ? erreur.code : null,
    effacerErreur: () => setErreur(null),
    creer,
    enregistrer,
    attacherCoffre,
    televerser,
    deposer,
    completer,
    retirer,
  };
}
